import React, { useRef, useState } from 'react';
import { X, Download, Copy, Check } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { MarkdownContent } from './MarkdownContent';
import { toPngDataUrl } from './chatFileLinkPng';
import '../../ChatbotStyles/ReportModal.scss';

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportContent: string;
  reportTitle: string;
  fileLinks?: string[];
}

export const ReportModal = ({
  isOpen,
  onClose,
  reportContent,
  reportTitle,
  fileLinks,
}: ReportModalProps) => {
  const reportRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(reportContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadPDF = async () => {
    if (!reportRef.current) return;

    const originalOverflow = reportRef.current.style.overflow;
    const originalMaxHeight = reportRef.current.style.maxHeight;
    const originalFontFamily = reportRef.current.style.fontFamily;

    reportRef.current.style.overflow = 'visible';
    reportRef.current.style.maxHeight = 'none';
    reportRef.current.style.fontFamily = 'Arial, sans-serif';

    try {
      // Calculate dynamic height to prevent page breaks
      const body = document.body;
      const html = document.documentElement;
      const element = reportRef.current;

      const height = Math.max(
        body.scrollHeight,
        body.offsetHeight,
        html.clientHeight,
        html.scrollHeight,
        html.offsetHeight,
        element.scrollHeight,
      );

      const heightCM = height / 35.35;

      const options = {
        margin: 1,
        filename: `${reportTitle.replace(/[^a-z0-9]/gi, '_')}.pdf`,
        image: { type: 'jpeg' as const, quality: 0.98 },
        html2canvas: {
          scale: 3,
          dpi: 300,
          letterRendering: true,
          useCORS: true,
          logging: false,
          allowTaint: false,
          backgroundColor: '#ffffff',
        },
        jsPDF: {
          orientation: 'portrait' as const,
          unit: 'cm' as const,
          format: [heightCM, 33] as [number, number],
          compress: true,
        },
      };

      await html2pdf().set(options).from(reportRef.current).save();
    } finally {
      reportRef.current.style.overflow = originalOverflow;
      reportRef.current.style.maxHeight = originalMaxHeight;
      reportRef.current.style.fontFamily = originalFontFamily;
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="report-modal-overlay" onClick={handleBackdropClick}>
      <div className="report-modal-container">
        <div className="report-modal-header">
          <h2 className="report-modal-title">Report viewer</h2>
          <button className="report-modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="report-modal-content" ref={reportRef}>
          <MarkdownContent content={reportContent} />

          {fileLinks && fileLinks.length > 0 && (
            <div className="report-images">
              {fileLinks.map((link, index) => {
                const imgSrc = toPngDataUrl(link);
                if (!imgSrc) {
                  return null;
                }
                return (
                  <div key={index} className="report-image-container">
                    <img
                      src={imgSrc}
                      alt={`Report image ${index + 1}`}
                      className="report-image"
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="report-modal-footer">
          <button
            className="report-copy-button"
            onClick={handleCopy}
            disabled={copied}
          >
            {copied ? <Check size={18} /> : <Copy size={18} />}
            {copied ? 'Copied' : 'Copy report'}
          </button>

          <button
            className="report-download-button"
            onClick={handleDownloadPDF}
          >
            <Download size={18} />
            Download as PDF
          </button>
        </div>
      </div>
    </div>
  );
};
